import { ResourceBudgetInfo } from './ResourceBudgetInfo';
import type { ResourceBudgetView } from '../../../api/src/queue/resource-budget.policy';
import { TaskResourceProfiles } from './TaskResourceProfiles';
import { useId, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Stack, Typography } from './mui-system';
import type {
  AdmissionReason,
  QueueAdmissionView,
} from '../../../api/src/queue/queue-admission.policy';

const reasons: Record<AdmissionReason, string> = {
  healthy: 'Ресурсов достаточно',
  memory: 'Мало свободной памяти',
  'memory-pressure': 'Задержки из-за нехватки памяти',
  'cpu-pressure': 'Высокое ожидание процессора',
  'io-pressure': 'Высокое ожидание диска',
  recovering: 'Проверяем восстановление ресурсов',
  telemetry: 'Не удалось измерить ресурсы',
  stale: 'Замер ресурсов устарел',
  stopped: 'Обработчик остановлен',
};
const mib = (bytes: number) => Math.round(bytes / 1024 / 1024).toLocaleString('ru-RU');
const time = (value: string) => new Date(value).toLocaleString('ru-RU');

export function QueueAdmissionStatus({
  value,
  profilesEnabled = false,
  budget,
}: {
  value: QueueAdmissionView;
  profilesEnabled?: boolean;
  budget?: ResourceBudgetView;
}) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const current = value.freshness === 'fresh' && value.record;
  const observe = current && current.mode === 'observe';
  const ready = current && current.state === 'ready';
  const budgetUnknown = budget && budget.freshness !== 'fresh';
  const budgetWaiting = budget?.mode === 'enforce' && budget.waiting.length > 0;
  const status =
    !current || (budgetUnknown && !observe)
      ? 'нет актуальных сведений'
      : observe
        ? 'наблюдение без блокировки'
        : budgetWaiting
          ? 'ожидание общего бюджета'
          : ready
            ? 'запуск разрешён'
            : 'ожидание ресурсов';
  return (
    <>
      <Button
        size="small"
        color={
          !current || (!observe && (!ready || budgetWaiting || budgetUnknown)) ? 'warning' : 'info'
        }
        startIcon={<InfoOutlinedIcon />}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        sx={{ alignSelf: 'flex-start' }}
      >
        {budget ? 'Фоновые ресурсы' : 'Общая очередь'} · {status}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby={titleId}
      >
        <DialogTitle id={titleId}>
          {budget ? 'Ресурсы фоновых операций' : 'Ресурсы общей очереди'}
        </DialogTitle>
        <DialogContent>
          {budget && <ResourceBudgetInfo value={budget} />}
          <QueueAdmissionInfo value={value} />
          {open && profilesEnabled && <TaskResourceProfiles />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function QueueAdmissionInfo({ value }: { value: QueueAdmissionView }) {
  const record = value.record;
  if (value.freshness !== 'fresh' || !record)
    return (
      <Alert severity="warning">
        Общая очередь: нет актуальных сведений о допуске задач.
        {record ? ` Последний замер: ${time(record.measuredAt)}.` : ''} Состояние обработчика
        неизвестно.
      </Alert>
    );
  const { metrics: m, policy: p } = record;
  const observe = record.mode === 'observe';
  const ready = record.state === 'ready';
  return (
    <Stack gap={2} sx={{ mt: 2 }}>
      <Alert severity={observe ? 'info' : ready ? 'success' : 'warning'}>
        <Typography fontWeight={700}>
          Состояние ресурсов ·{' '}
          {observe
            ? 'наблюдение без блокировки'
            : ready
              ? 'нагрузка допустима'
              : 'новые задачи ожидают ресурсы'}
        </Typography>
        {reasons[record.reason]}.
        {record.state === 'recovering'
          ? ` Устойчивых замеров: ${record.healthySamples}/${p.recoverySamples}.`
          : ''}
        {!observe && !ready && record.pausedSince ? ` Ожидание с ${time(record.pausedSince)}.` : ''}{' '}
        Замер: {time(record.measuredAt)}.
      </Alert>
      <Typography fontWeight={700}>Ресурсы и условия запуска</Typography>
      <Stack gap={1}>
        {m && (
          <>
            <Typography>
              Память: запас {mib(m.memoryAvailableBytes)} МиБ при лимите{' '}
              {mib(m.memoryCapacityBytes)} МиБ. Доступно CPU: {m.cpuCapacity}.
            </Typography>
            <Typography>
              Ожидание ресурсов за 10 секунд: CPU {m.cpuPressure}%, память {m.memoryPressure}%, диск{' '}
              {m.ioPressure}%.
            </Typography>
            <Typography>
              Пауза при запасе памяти ≤ {p.memoryStopRatio * 100}% (
              {mib(m.memoryCapacityBytes * p.memoryStopRatio)} МиБ). Возобновление при запасе ≥{' '}
              {p.memoryResumeRatio * 100}% ({mib(m.memoryCapacityBytes * p.memoryResumeRatio)} МиБ).
            </Typography>
          </>
        )}
        {record.error && <Typography>Причина отсутствия замера: {record.error}</Typography>}
        <Typography>
          Пауза при ожидании CPU ≥ {p.cpuPressureStop}%, памяти ≥ {p.memoryPressureStop}% или диска
          ≥ {p.ioPressureStop}%. Для возобновления нужны {p.recoverySamples} последовательных
          замера: CPU &lt; {p.cpuPressureResume}%, память &lt; {p.memoryPressureResume}%, диск &lt;{' '}
          {p.ioPressureResume}% и достаточный запас памяти.
        </Typography>
        <Typography color="text.secondary">
          Замеры используются для допуска очереди, доноров и материализатора. Текущая работа
          продолжается; попытки ожидающих задач не расходуются. Дополнительные ограничения доноров и
          материализатора сохраняются.
        </Typography>
      </Stack>
    </Stack>
  );
}
